import { createFileRoute } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAudit } from '@/api/queries';
import { Empty, ErrorBox } from '@/components/empty';
import { Json } from '@/components/json-view';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export const Route = createFileRoute('/_authed/audit')({
  component: AuditPage,
});

function AuditPage() {
  const { t } = useTranslation();
  const [actionPrefix, setActionPrefix] = useState('');
  const [targetType, setTargetType] = useState('');
  const [targetId, setTargetId] = useState('');
  const audit = useAudit({
    action_prefix: actionPrefix || undefined,
    target_type: targetType || undefined,
    target_id: targetId || undefined,
    limit: 200,
  });
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('audit.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('audit.subtitle')}</p>
      </header>

      <div className="grid gap-3 md:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="action">{t('audit.filter_action')}</Label>
          <Input
            id="action"
            placeholder="gateway_config."
            value={actionPrefix}
            onChange={(e) => setActionPrefix(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ttype">{t('audit.filter_target_type')}</Label>
          <Input
            id="ttype"
            placeholder="merchant"
            value={targetType}
            onChange={(e) => setTargetType(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="tid">{t('audit.filter_target_id')}</Label>
          <Input
            id="tid"
            placeholder="UUID"
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
          />
        </div>
      </div>

      {audit.isError && <ErrorBox message={t('common.error')} />}

      <div className="rounded-md border border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('audit.cols.time')}</TableHead>
              <TableHead>{t('audit.cols.actor')}</TableHead>
              <TableHead>{t('audit.cols.action')}</TableHead>
              <TableHead>{t('audit.cols.target')}</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {audit.data?.map((row) => (
              <Fragment key={row.id}>
                <TableRow
                  className="cursor-pointer"
                  onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                >
                  <TableCell className="font-mono text-xs whitespace-nowrap">
                    {dayjs(row.created_at).format('MMM D HH:mm:ss')}
                  </TableCell>
                  <TableCell className="text-xs">
                    <Badge variant="secondary">{row.actor_type}</Badge>
                    <span className="ms-1 font-mono text-muted-foreground">
                      {row.actor_id ? `${row.actor_id.slice(0, 8)}…` : '—'}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{row.action}</TableCell>
                  <TableCell className="text-xs">
                    <Badge variant="outline">{row.target_type}</Badge>
                    <span className="ms-1 font-mono text-muted-foreground">
                      {row.target_id ? `${row.target_id.slice(0, 8)}…` : '—'}
                    </span>
                  </TableCell>
                  <TableCell className="text-end text-xs text-muted-foreground">
                    {expanded === row.id ? '−' : '+'}
                  </TableCell>
                </TableRow>
                {expanded === row.id && (
                  <TableRow>
                    <TableCell colSpan={5} className="bg-muted/30">
                      <div className="grid gap-4 md:grid-cols-2 p-2">
                        <div>
                          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                            {t('audit.before')}
                          </p>
                          <Json data={row.before} />
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                            {t('audit.after')}
                          </p>
                          <Json data={row.after} />
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
        {!audit.isLoading && audit.data?.length === 0 && (
          <Empty title={t('audit.no_results')} className="border-0" />
        )}
      </div>
    </div>
  );
}
